/* @jsx h */

import {
  calculateOrderTotal,
  formatPrice,
  getOrderDisplayName,
} from "@lemmonhq/orders";
import { Order } from "@lemmonhq/types";

import { computed, defineComponent, ref, h } from "vue";
import { useLocalization } from "@lemmonhq/uikit";
import { AcceptOrderModal } from "./Accept";
import { RejectOrderModal } from "./Reject";
import { useAuth } from "@app/vue/providers/Auth";

import { OrderCardPickupInfo } from "../OrderCardPickupInfo";
import { OrderCardDeliveryInfo } from "../OrderCardDeliveryInfo";

export interface PendingOrderCardProps {
  order: Order;
  onOrderUpdate: (order: Order) => void;
}

export const PendingOrderCard = defineComponent<PendingOrderCardProps>({
  name: "PendingOrderCard",
  props: ["order", "onOrderUpdate"],
  setup(props) {
    const auth = useAuth();
    const { t } = useLocalization();
    const showAcceptModal = ref(false);
    const showRejectModal = ref(false);

    const formattedDate = computed(() =>
      new Date(props.order.created_at).toLocaleDateString("fr-CH"),
    );

    const formattedTime = computed(() =>
      new Date(props.order.created_at).toLocaleTimeString("fr-CH", {
        hour: "numeric",
        minute: "numeric",
        hour12: true,
      }),
    );

    return () => (
      <>
        {/* Order Details */}
        <div class="px-3 py-3 flex-1 space-y-3 ">
          <div class="flex justify-between items-start">
            <div>
              <div class="font-bold text-accent text-sm">
                {getOrderDisplayName(props.order)}
              </div>
              <div class="text-xs" title={formattedDate.value}>
                {formattedTime.value}
              </div>
            </div>
            <div class="text-right text-xs">
              {props.order.table && (
                <div class="font-bold">{props.order.table.name}</div>
              )}
              <div class=" font-semibold">
                {formatPrice(
                  calculateOrderTotal(props.order),
                  true,
                  2,
                  auth.user.value?.restaurant.currency || "CHF",
                )}
              </div>
            </div>
          </div>

          {/* Order Items */}
          <div class="border border-[var(--order-card-section-border)] px-2 py-2 ">
            {props.order.items && props.order.items.length > 0 && (
              <div class="space-y-2">
                {props.order.items.map((item) => (
                  <div key={item.id} class="flex items-center gap-2 text-xs">
                    <div class="bage badge-accent rounded px-1.5 py-0.5 text-xs font-bold flex-shrink-0">
                      {item.quantity}
                    </div>
                    <div class="flex-1">
                      <div class="text-accent">{item.product.name}</div>
                      {item.notes && (
                        <div class=" opacity-75">
                          {t("orders.card.note")} {item.notes}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {props.order.notes ? (
            <div class="border border-[var(--order-card-section-border)] px-2 py-2 ">
              <div class="font-semibold text-accent">
                {t("orders.card.notes")}
              </div>
              {props.order.notes}
            </div>
          ) : null}

          {props.order.pickup && (
            <OrderCardPickupInfo pickup={props.order.pickup} />
          )}

          {props.order.delivery && (
            <OrderCardDeliveryInfo delivery={props.order.delivery} />
          )}

          {/* Action Buttons */}
          <div class="flex gap-2 pt-2">
            <button
              onClick={() => (showAcceptModal.value = true)}
              class="btn btn-sm btn-success text-accent flex-1 font-semibold"
            >
              <span class="material-symbols-outlined text-1xl">
                check_circle
              </span>
            </button>
            <button
              onClick={() => (showRejectModal.value = true)}
              class="btn btn-sm btn-error flex-1  font-semibold"
            >
              <span class="material-symbols-outlined text-1xl">cancel</span>
            </button>
          </div>
        </div>
        {showAcceptModal.value ? (
          <AcceptOrderModal
            onClose={() => (showAcceptModal.value = false)}
            order={props.order}
            onConfirm={(order) => props.onOrderUpdate(order)}
          />
        ) : null}
        {/* Modals */}
        {showRejectModal.value ? (
          <RejectOrderModal
            onClose={() => (showRejectModal.value = false)}
            order={props.order}
            onConfirm={(order) => props.onOrderUpdate(order)}
          />
        ) : null}
      </>
    );
  },
});
